import React, { useEffect, useRef, useState } from 'react';
import { client, consoleLogInDev, isValidPhone, validateEmail } from 'shared';
import { ErrorText, FieldGroup, Grid, Modal, PhoneInput, PillButton, Row, Stack, Text, TextInput } from 'library/ui';
import { IBusinessLocation, PagingResult } from 'models';
import { useUserContext } from 'UserContext';
import { IPermission, Role } from './models';
import LocationPermissions, { ALL_LOCATIONS_KEY, PermissionsByLocation } from './LocationPermissions';
import {
    buildPermissionPayload,
    copyPermissionsToLocations,
    IAllLocationsSnapshot,
    enterAllLocationsMode,
    leaveAllLocationsMode,
    togglePermission,
} from './permissionsByLocation';
import SelectLocations from './SelectLocations';
import SelectRoles from './SelectRoles';
import { Add } from '@mui/icons-material';

interface IAddUserProps {
    onContactAdded: () => void;
}

interface IUserInfo {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    email: string;
}

export default function AddUser({ onContactAdded }: IAddUserProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [userInfo, setUserInfo] = useState<IUserInfo>();
    const [selectedLocations, setSelectedLocations] = useState<number[]>([]);
    const [allLocationsSelected, setAllLocationsSelected] = useState(false);
    const allLocationsSnapshot = useRef<IAllLocationsSnapshot | null>(null);
    const defaultsApplied = useRef(false);
    const [permissionsByLocation, setPermissionsByLocation] = useState<PermissionsByLocation>({});
    const [selectedRoles, setSelectedRoles] = useState<number[]>([]);
    const [availableLocations, setAvailableLocations] = useState<IBusinessLocation[]>([]);
    const [availablePermissions, setAvailablePermissions] = useState<IPermission[]>([]);
    const [availableRoles, setAvailableRoles] = useState<Role[]>([]);

    const { user } = useUserContext();

    useEffect(() => {
        if (!isOpen || (availableLocations.length && availablePermissions.length)) {
            return;
        }

        client(`api/business-location/?page_size=100`)
            .then((response: PagingResult<IBusinessLocation>) => setAvailableLocations(response.results))
            .catch((error) => {
                consoleLogInDev(error);
            });
        client(`api/permissions/?page_size=50`)
            .then((response: PagingResult<IPermission>) => setAvailablePermissions(response.results))
            .catch((error) => {
                consoleLogInDev(error);
            });
        client(`api/role/?type=BUSINESS_CONTACT&page_size=50`)
            .then((response: PagingResult<Role>) => setAvailableRoles(response.results))
            .catch((error) => {
                consoleLogInDev(error);
            });
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen || defaultsApplied.current || !availableLocations.length || !availablePermissions.length) {
            return;
        }
        defaultsApplied.current = true;
        setAllLocationsSelected(true);
        setSelectedLocations(availableLocations.map((x) => x.id));
        setPermissionsByLocation({ [ALL_LOCATIONS_KEY]: availablePermissions.map((x) => x.id) });
    }, [isOpen, availableLocations, availablePermissions]);

    function onSubmitClick() {
        setErrorMessage('');
        setIsSubmitting(true);
        client(`api/business-contact/`, {
            body: {
                first_name: userInfo?.firstName,
                last_name: userInfo?.lastName,
                email: userInfo?.email,
                phone_number: userInfo?.phoneNumber ? userInfo.phoneNumber : undefined,
                ...buildPermissionPayload(selectedLocations, allLocationsSelected, permissionsByLocation),
                business_id: user?.businesses_active?.[0].id,
                roles: selectedRoles,
            },
        })
            .then(() => {
                onContactAdded();
                closeModal();
            })
            .catch((error) => {
                if (error.email) {
                    setErrorMessage(error.email.join(' '));
                } else if (error.phone_number) {
                    setErrorMessage(error.phone_number.join(' '));
                } else {
                    setErrorMessage(' An error occurred while creating the contact');
                }
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    }

    function onUserInfoChange(value: string, key: keyof IUserInfo) {
        setUserInfo((prevVal) => {
            return {
                ...(prevVal || {}),
                [key]: value,
            } as IUserInfo;
        });
    }

    function onPermissionToggle(locationKey: number, permissionId: number) {
        setPermissionsByLocation((prev) => togglePermission(prev, locationKey, permissionId, availablePermissions));
    }

    function onCopyPermissionsToAll(fromLocationKey: number) {
        setPermissionsByLocation((prev) => copyPermissionsToLocations(prev, fromLocationKey, selectedLocations));
    }

    function onRoleSelect(id: number) {
        if (selectedRoles.includes(id)) {
            setSelectedRoles(selectedRoles.filter((x) => x !== id));
        } else {
            setSelectedRoles([...selectedRoles, id]);
        }
    }

    function onLocationSelectAll() {
        const allIds = availableLocations.map((x) => x.id);
        if (allLocationsSelected) {
            const snapshot = allLocationsSnapshot.current;
            const restoredLocations = snapshot ? snapshot.selected.filter((id) => allIds.includes(id)) : [];
            setAllLocationsSelected(false);
            setSelectedLocations(restoredLocations.length ? restoredLocations : allIds);
            setPermissionsByLocation((prev) => leaveAllLocationsMode(prev, allIds, snapshot));
            allLocationsSnapshot.current = null;
        } else {
            const { next, snapshot } = enterAllLocationsMode(permissionsByLocation, selectedLocations);
            allLocationsSnapshot.current = snapshot;
            setAllLocationsSelected(true);
            setSelectedLocations(allIds);
            setPermissionsByLocation(next);
        }
    }

    const isPhoneValid = !userInfo?.phoneNumber || isValidPhone(userInfo.phoneNumber);
    const isValidEmail = userInfo?.email && validateEmail(userInfo.email);
    const showEmailError = userInfo?.email ? !validateEmail(userInfo.email) : false;

    function isSubmitDisabled() {
        return (
            !userInfo?.firstName ||
            !userInfo?.lastName ||
            !isValidEmail ||
            (!allLocationsSelected && !selectedLocations.length) ||
            !isPhoneValid ||
            isSubmitting
        );
    }

    function closeModal() {
        setIsOpen(false);
        setUserInfo(undefined);
        setSelectedLocations([]);
        setAllLocationsSelected(false);
        allLocationsSnapshot.current = null;
        defaultsApplied.current = false;
        setPermissionsByLocation({});
    }

    return (
        <>
            <PillButton icon={<Add sx={{ fontSize: 18 }} />} onClick={() => setIsOpen(true)}>
                Add User
            </PillButton>
            {isOpen && (
                <Modal title="Add User" onClose={closeModal} width={700}>
                    <Stack gap="md">
                        <Text weight="semibold">Account Information</Text>
                        <Grid columns={2} gap="md" collapseAt="sm">
                            <FieldGroup label="First Name" required>
                                <TextInput
                                    fullWidth
                                    value={userInfo?.firstName || ''}
                                    onChange={(value) => onUserInfoChange(value, 'firstName')}
                                />
                            </FieldGroup>
                            <FieldGroup label="Last Name" required>
                                <TextInput
                                    fullWidth
                                    value={userInfo?.lastName || ''}
                                    onChange={(value) => onUserInfoChange(value, 'lastName')}
                                />
                            </FieldGroup>
                            <FieldGroup label="Email" required>
                                <TextInput
                                    fullWidth
                                    value={userInfo?.email || ''}
                                    onChange={(value) => onUserInfoChange(value, 'email')}
                                />
                                {showEmailError && <ErrorText>Invalid email</ErrorText>}
                            </FieldGroup>
                            <FieldGroup label="Phone Number">
                                <PhoneInput
                                    fullWidth
                                    value={userInfo?.phoneNumber || ''}
                                    onChange={(e164) => onUserInfoChange(e164, 'phoneNumber')}
                                />
                                {!isPhoneValid && <ErrorText>Invalid Format</ErrorText>}
                            </FieldGroup>
                        </Grid>
                        <SelectLocations
                            availableLocations={availableLocations}
                            selectedLocations={selectedLocations}
                            allLocationsSelected={allLocationsSelected}
                            onLocationsChange={setSelectedLocations}
                            onSelectAll={onLocationSelectAll}
                        />
                        <LocationPermissions
                            availableLocations={availableLocations}
                            selectedLocations={selectedLocations}
                            allLocationsSelected={allLocationsSelected}
                            availablePermissions={availablePermissions}
                            permissionsByLocation={permissionsByLocation}
                            onToggle={onPermissionToggle}
                            onCopyToAll={onCopyPermissionsToAll}
                        />
                        <SelectRoles
                            selectedRoles={selectedRoles}
                            onRoleSelect={onRoleSelect}
                            availableRoles={availableRoles}
                        />
                        <Row justify="end" gap="sm">
                            <PillButton variant="secondary" onClick={closeModal}>
                                Cancel
                            </PillButton>
                            <PillButton disabled={isSubmitDisabled()} onClick={onSubmitClick}>
                                {isSubmitting ? 'Adding...' : 'Add'}
                            </PillButton>
                        </Row>
                        {errorMessage && <Text tone="error">{errorMessage}</Text>}
                    </Stack>
                </Modal>
            )}
        </>
    );
}
